import { Box, Stack, Typography } from "@mui/material"
import { FC } from "react"
import { ReservationSyncDto } from "src/features/things/reservationSyncApi"
import {
  NON_DRIFT_KEYS,
  NON_DRIFT_LABEL,
  NON_DRIFT_NOTE,
  type NonDriftKey,
} from "./driftClasses"
import { fmtCount } from "./ReservationSyncChrome"

/**
 * Counted, never drift (LOCKER-421 `DoD4`, `DoD8`).
 *
 * Six defect classes of their own. Each is counted in full and **none of them is
 * ever summed into the drift total** — calling a known-bad row drift would put it
 * in a queue of fixable ones, and 1.6% of production carries a poisoned expiry
 * that no sync can correct.
 *
 * They are shown as a separate panel rather than as more chips beside the drift
 * classes, for the same reason the filters are split into two groups: today's flat
 * row gives a defect class and a divergence identical weight, and the operator has
 * to already know the difference to read it correctly.
 *
 * `knownProjectionDefect` gets its own full-width card because it is the one whose
 * count is expected to be large and is expected to SHRINK — every row a sync
 * touches for any other reason repairs its projection as a side effect, so the
 * number is a progress indicator rather than a backlog. `partnerBlankField`
 * (LOCKER-437) is wide for the same reason: large (305,770 field diffs in prod on
 * 2026-09-23) and expected to shrink once SST restores the dropped times.
 */

const isLoud = (key: NonDriftKey) =>
  key === "poisonedExpiry" || key === "knownProjectionDefect"

const ClassCard: FC<{ name: NonDriftKey; count: number | null; wide?: boolean }> = ({
  name,
  count,
  wide,
}) => (
  <Box
    sx={{
      border: 1,
      borderColor: count && isLoud(name) ? "warning.main" : "divider",
      borderRadius: 1,
      p: 1.75,
      gridColumn: wide ? "1 / -1" : undefined,
      minWidth: 0,
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="baseline">
      <Typography
        sx={{
          fontFamily: "monospace",
          fontSize: "1.3rem",
          fontWeight: 500,
          color: count ? (isLoud(name) ? "warning.dark" : "text.primary") : "text.disabled",
        }}
      >
        {fmtCount(count)}
      </Typography>
      <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
        {NON_DRIFT_LABEL[name]}
      </Typography>
    </Stack>
    <Typography
      variant="caption"
      sx={{ display: "block", mt: 0.75, color: "text.secondary", lineHeight: 1.5 }}
    >
      {count === null
        ? name === "partnerBlankField"
          ? "Not counted — this run predates the flag (LOCKER-437). Re-run the sweep to get it."
          : "Not counted — the OLAP leg was not read on this run, so the honest answer is unknown rather than zero."
        : NON_DRIFT_NOTE[name]}
    </Typography>
  </Box>
)

export const CountedNeverDrift: FC<{ run: ReservationSyncDto }> = ({ run }) => {
  if (run.nonDriftCounts === null)
    return (
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        No class counts are shown for an inconclusive run — including these. They
        would describe rows nobody read.
      </Typography>
    )

  if (!run.nonDriftCounts)
    return (
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        This run predates the counted-never-drift classes. Re-run the sweep to get
        them.
      </Typography>
    )

  const counts = run.nonDriftCounts
  const wide: NonDriftKey[] = ["knownProjectionDefect", "partnerBlankField"]
  const narrow = NON_DRIFT_KEYS.filter((k) => !wide.includes(k))

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: "0.88rem", fontWeight: 600 }}>
          Counted, never drift
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          excluded from the drift total of {fmtCount(run.driftTotal)} — each is a
          defect class of its own, not a divergence
        </Typography>
      </Stack>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
          gap: 1.5,
        }}
      >
        {narrow.map((key) => (
          <ClassCard key={key} name={key} count={counts[key] ?? null} />
        ))}
        {wide.map((key) => (
          <ClassCard key={key} name={key} count={counts[key] ?? null} wide />
        ))}
      </Box>
    </Box>
  )
}
